
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationProvider } from 'vee-validate';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import TextField from '@/components/shared/TextField.vue';
import '@/validation-rules';

@Component<School>({
	components: {
		TextField,
		ValidationProvider,
		InfoBanner,
		TextButton,
		IconButton
	}
})
export default class School extends Vue {
	@Prop({ type: Object, default: () => {} }) private targetDeliveryInfo!: Delivery;

	private mutableDeliveryInfo: Delivery = this.targetDeliveryInfo;
	private areGradesClassroomsEnabled: boolean = false;
	private areTeachersEnabled: boolean = false;
	private isExtraEnabled: boolean = false;

	private mounted(): void {
		// Set the initial values for the switches
		this.areGradesClassroomsEnabled = this.mutableDeliveryInfo.schools?.some((school) => school.grades || school.classrooms) ?? false;
		this.areTeachersEnabled = this.mutableDeliveryInfo.schools?.some((school) => school.teachers !== undefined) ?? false;
		this.isExtraEnabled = this.mutableDeliveryInfo.schools?.some((school) => school.extra !== undefined) ?? false;

		// Add the isTeachersOpenEnded property to each school object
		this.mutableDeliveryInfo.schools?.forEach((school) => Vue.set(school, 'isTeachersOpenEnded', school.teachers === ''));
	}

	/**
	 * Either set the classrooms and grades to empty string or delete the properties them from the delivery object
	 * depending on the switch state.
	 *
	 * @returns {void}
	 */
	private onGradesClassroomsToggle(): void {
		if (this.areGradesClassroomsEnabled) {
			this.mutableDeliveryInfo.schools?.forEach((school) => {
				school.grades = '';
				school.classrooms = '';
			});
		}
		else {
			this.mutableDeliveryInfo.schools = this.mutableDeliveryInfo.schools?.map(({ grades, classrooms, ...rest }) => rest);
		}
	}

	/**
	 * Either set the teachers to empty string or delete the properties them from the delivery object
	 * depending on the switch state.
	 *
	 * @returns {void}
	 */
	private onTeachersToggle(): void {
		this.areTeachersEnabled
			? this.mutableDeliveryInfo.schools?.forEach((school) => school.teachers = '')
			: this.mutableDeliveryInfo.schools = this.mutableDeliveryInfo.schools?.map(({ teachers, ...rest }) => rest);
	}

	/**
	 * Either set the extra to empty string or delete the property from the delivery object
	 * depending on the switch state.
	 *
	 * @returns {void}
	 */
	private onExtraToggle(): void {
		this.isExtraEnabled
			? this.mutableDeliveryInfo.schools?.forEach((school) => school.extra = '')
			: this.mutableDeliveryInfo.schools = this.mutableDeliveryInfo.schools?.map(({ extra, ...rest }) => rest);
	}

	/**
	 * Set the teachers values to their initial value.
	 *
	 * @param {number} index - The index of the school in the schools array
	 * @return {void}
	 */
	private resetTeachersInput(index: number): void {
		if (this.mutableDeliveryInfo.schools?.[index]) {
			this.mutableDeliveryInfo.schools[index].teachers = '';
		}
	}

	/**
	 * Delete a school from the schools array.
	 *
	 * @param {number} index
	 * @return {void}
	 */
	private deleteSchool(index: number): void {
		this.mutableDeliveryInfo.schools?.splice(index, 1);
	}

	/**
	 * Add a new school object containing the correct properties to the schools array.
	 *
	 * @return {void}
	 */
	private addSchool(): void {
		this.mutableDeliveryInfo.schools?.push({
			id: Date.now(),
			name: '',
			isTeachersOpenEnded: false,
			...(this.areGradesClassroomsEnabled && { grades: '', classrooms: '' }),
			...(this.areTeachersEnabled && { teachers: '' }),
			...(this.isExtraEnabled && { extra: '' })
		});
	}
}
